import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  REST_ID,
  chainOf,
  chainScheme,
  kgText,
  lineKey,
  loadText,
  parseLoadField,
  parseSchemeField,
  partName,
  pmKey,
  videoLink,
  workKg,
  type AbsUnit,
  type ChainLine,
  type ExLine,
  type Exercise,
  type Line,
  type Load,
} from '@tg/domain';
import { AIMARK, DAYICON, ICON } from './icons';
import { Svg } from './Svg';
import { useCtx, fmtN, fw, type Ctx } from './ctx';
import * as M from './model';
import { Suggest, sugRect, type SugState } from './Suggest';
import { AddVideoModal, EXT_SVG, openLink, useExLink, useExVideo, useVideoUpload, VideoModal } from '../ui/Video';
import { Modal } from '../ui/Modal';
import { mediaUrl } from '@tg/app-core';
import { useMe } from '../lib/data';

const VOL_UNITS: AbsUnit[] = ['м', 'сек', 'мин', 'кал'];
const RNG_TIP = 'Диапазон «от–до»: 70–80 % или 60–70 кг. Можно и набрать через дефис';
/* К числу добавляется «–» и курсор встаёт за ним (CON-24). Хвостовой дефис сначала срезаем —
   иначе повторный клик до того, как поле потеряло фокус, копит дефисы («6––»). */
const appendRngDash = (v: string) => v.trim().replace(/\s*[-–—]\s*$/, '') + '–';
function focusAtEnd(el: HTMLInputElement | null) {
  if (!el) return;
  el.focus();
  const n = el.value.length;
  el.setSelectionRange(n, n);
}

/** Единицы строки и текущая единица (как loadInfo в прототипе) */
export function loadInfo(it: Line, c: Pick<Ctx, 'catalog' | 'pm' | 'isGroup' | 'unitSel'>) {
  const key = lineKey(it, c.pm, c.catalog.ex);
  const weighted = !!key;
  const exU =
    it.t === 'chain' ? it.parts.flatMap((p) => (p.exId ? c.catalog.ex(p.exId)?.units || [] : [])) : (it.exId && c.catalog.ex(it.exId)?.units) || [];
  const units: string[] = [...new Set([...(weighted ? ['%', 'кг'] : []), ...exU.filter((u) => VOL_UNITS.includes(u))])];
  if (it.load?.kind === 'abs' && !units.includes(it.load.unit)) units.push(it.load.unit);
  const def = weighted ? (c.pm[key!] || c.isGroup ? '%' : 'кг') : units[0] || '';
  const cur = it.load ? (it.load.kind === 'pct' ? '%' : it.load.unit) : units.includes(c.unitSel[it.id]) ? c.unitSel[it.id] : def;
  return { key, weighted, units, cur, has: units.length > 0 };
}

const ldVal = (l: Load | null | undefined) => (!l ? '' : fmtN(l.from) + (l.to != null ? '–' + fmtN(l.to) : ''));

/** Нагрузка из поля в модель */
function applyLoad(raw: string, cur: string): { load: Load | null } | { text: string } {
  const r = parseLoadField(raw);
  if ('empty' in r) return { load: null };
  if ('text' in r) return { text: r.text };
  const u = r.unit || cur;
  if (u === '%') return { load: { kind: 'pct', from: r.from, ...(r.to != null ? { to: r.to } : {}) } };
  return { load: { kind: 'abs', unit: (u || 'повт') as AbsUnit, from: r.from, ...(r.to != null ? { to: r.to } : {}) } };
}

/* Поля связки видны только при :focus-within (CSS прототипа). В Safari между уходом из одного поля и входом
   в другое связка на миг «без фокуса»: если в этот миг перерисовать (сохранить правку), поля прячутся и клик
   по соседнему полю уходит мимо — курсор не встаёт, введённое теряется. Поэтому правку поля связки
   сохраняем после того, как фокус дошёл до нового поля. */
const afterFocus = (fn: () => void) => setTimeout(fn);

/* ─── Поле, которое не теряет курсор при перерисовке: значение из модели, пока не в фокусе ─── */
function useField(modelValue: string) {
  const [v, setV] = useState(modelValue);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setV(modelValue);
  }, [modelValue]);
  return {
    v,
    setV,
    bind: {
      onFocus: () => (focused.current = true),
      onBlurCapture: () => (focused.current = false),
    },
  };
}

/** Фокус по запросу (после Enter, выбора вида строки и т. п.) */
function useAutoFocus(ref: React.RefObject<HTMLElement | null>, id: string, field: string) {
  const { focus, setFocus } = useCtx();
  useLayoutEffect(() => {
    if (focus && focus.id === id && focus.field === field && ref.current) {
      ref.current.focus();
      if (ref.current.isContentEditable) {
        const s = window.getSelection();
        s?.selectAllChildren(ref.current);
        s?.collapseToEnd();
      }
      setFocus('', '');
    }
  }, [focus, id, field]);
}

/* ─── Параметры строки: схема, нагрузка, от–до, рабочий вес, 1ПМ ─── */

function Params({ it, schPh }: { it: ExLine; schPh: string }) {
  const c = useCtx();
  const L = loadInfo(it, c);
  const kg = kgText(workKg(it, c.pm, c.catalog.ex));
  const sch = useField(it.txt || it.scheme || '');
  const ld = useField(ldVal(it.load));
  const ldRef = useRef<HTMLInputElement>(null);
  const schRef = useRef<HTMLInputElement>(null);
  const [pmv, setPm] = useState('');
  useAutoFocus(schRef, it.id, 'sch');
  useAutoFocus(ldRef, it.id, 'ld');
  const hasRange = it.load?.to != null;

  const commitSch = (v: string) => {
    const r = parseSchemeField(v);
    /* Непонятное («4до10/10», «до отказа», «3 круга») остаётся в схеме как написано — строка не уходит в текст
       и поле нагрузки не пропадает: буквы ничего не ломают */
    if ('txt' in r) c.update((d) => M.setLine(d, it.id, { txt: undefined, scheme: r.txt }));
    else c.update((d) => M.setLine(d, it.id, { txt: undefined, scheme: r.scheme || undefined, ...(r.load ? { load: r.load } : {}) }));
  };
  const commitLd = (v: string) => {
    const r = applyLoad(v, L.cur);
    /* Нагрузка не числом («лёгкий», «по самочувствию») — текст дописывается в схему, поле нагрузки остаётся */
    if ('text' in r) {
      ld.setV(ldVal(it.load));
      c.update((d) => M.setLine(d, it.id, { txt: undefined, scheme: [it.txt || it.scheme, r.text].filter(Boolean).join(' '), load: undefined }));
      c.toast('В нагрузке — только число; текст «' + r.text + '» перенесён в схему');
    }
    else c.update((d) => M.setLine(d, it.id, { load: r.load ?? undefined }));
  };
  const cycleUnit = () => {
    const i = L.units.indexOf(L.cur);
    const next = L.units[(i + 1) % L.units.length];
    c.unitSel[it.id] = next;
    if (it.load) commitLdWith(next);
    else c.update((d) => d);
  };
  const commitLdWith = (unit: string) => {
    if (!it.load) return;
    const load: Load = unit === '%' ? { kind: 'pct', from: it.load.from, to: it.load.to } : { kind: 'abs', unit: unit as AbsUnit, from: it.load.from, to: it.load.to };
    if (load.to == null) delete load.to;
    c.update((d) => M.setLine(d, it.id, { load }));
  };
  const toggleRange = () => {
    if (hasRange && it.load) {
      const { to: _to, ...rest } = it.load;
      c.update((d) => M.setLine(d, it.id, { load: rest as Load }));
    } else {
      ld.setV(appendRngDash(ld.v || ''));
      requestAnimationFrame(() => focusAtEnd(ldRef.current));
    }
  };
  const nav = (e: KeyboardEvent<HTMLInputElement>, next?: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
      next?.();
    } else if (e.key === 'Escape') (e.target as HTMLInputElement).blur();
  };
  const newLine = () => {
    let id = '';
    c.update((d) => {
      const [n, nid] = M.addLine(d, { after: it.id });
      id = nid;
      return n;
    });
    setTimeout(() => c.setFocus(id, 'e'));
  };

  const rest = it.exId === REST_ID;
  /* Повторы без единицы — подпись «раз»: видно, что число — это повторы (а у нагрузки — своя единица) */
  const reps = !rest && !it.txt && /(^|×)\d+(?:[-/]\d+)*$/.test(sch.v.trim());

  return (
    <>
      {/* У отдыха схемы нет — только время */}
      {!rest && (
        <input
          ref={schRef}
          className={'pf sch' + (!sch.v ? ' nos' : '')}
          data-pf="sch"
          data-for={it.id}
          value={sch.v}
          placeholder={schPh}
          autoComplete="off"
          spellCheck={false}
          style={{ width: fw(sch.v, schPh.length > 3 ? 4 : 3, 20) }}
          onChange={(e) => sch.setV(e.target.value)}
          {...sch.bind}
          onBlur={(e) => commitSch(e.target.value)}
          onKeyDown={(e) => nav(e, newLine)}
        />
      )}
      {reps && <span className="schu">раз</span>}
      {L.has && (
        <>
          <span className={'pf ld ' + (ld.v ? '' : 'empty')}>
            <input
              ref={ldRef}
              data-pf="ld"
              data-for={it.id}
              value={ld.v}
              placeholder={L.weighted ? 'вес' : 'объём'}
              inputMode="decimal"
              autoComplete="off"
              spellCheck={false}
              style={{ width: fw(ld.v, 4, 4) }}
              onChange={(e) => ld.setV(e.target.value)}
              {...ld.bind}
              onBlur={(e) => commitLd(e.target.value)}
              onKeyDown={(e) => nav(e, newLine)}
            />
            <u data-pfu={it.id} title="Сменить единицу" onMouseDown={(e) => e.preventDefault()} onClick={cycleUnit}>
              {L.cur}
            </u>
          </span>
          <button className={'rng ' + (hasRange ? 'on' : '')} data-rng={it.id} tabIndex={-1} title={hasRange ? 'Убрать диапазон — оставить одно число' : RNG_TIP} onMouseDown={(e) => e.preventDefault()} onClick={toggleRange}>
            от–до
          </button>
        </>
      )}
      <span className="kg">{kg != null ? '→ ' + kg + ' кг' : c.isGroup && it.load?.kind === 'pct' ? 'от 1ПМ каждого' : ''}</span>
      {L.weighted && (
        <span className={'pmf ' + (L.cur === '%' && !c.pm[L.key!] && !c.isGroup ? '' : 'off')}>
          1ПМ клиента{' '}
          <span className="pf pm">
            <input
              data-pf="pm"
              data-for={it.id}
              placeholder="—"
              inputMode="decimal"
              autoComplete="off"
              value={pmv}
              style={{ width: fw(pmv, 3, 4) }}
              onChange={(e) => setPm(e.target.value)}
              onBlur={() => {
                const kgv = parseFloat(pmv.replace(',', '.'));
                if (kgv > 0 && L.key) c.setMax(L.key, kgv);
                setPm('');
              }}
              onKeyDown={(e) => nav(e)}
            />
            <u>кг</u>
          </span>
        </span>
      )}
    </>
  );
}

/* ─── Поле названия с подсказками (общая логика для строки и «+ упражнение» связки) ─── */

interface NameOpts {
  id: string;
  field: string;
  initial: string;
  /** Выбор из базы */
  onPick: (e: Exercise) => void;
  /** Enter без выбора: текст как написан */
  onEnter: (text: string) => void;
  /** Уход из поля */
  onCommit?: (text: string) => void;
  onPaste?: (text: string) => boolean;
  onPlus?: () => boolean;
  ph?: string;
  className: string;
}

function NameEdit(o: NameOpts & { children?: React.ReactNode }) {
  const c = useCtx();
  const ref = useRef<HTMLSpanElement>(null);
  const [sug, setSug] = useState<SugState | null>(null);
  useAutoFocus(ref, o.id, o.field);
  const text = () => (ref.current?.textContent || '').replace(/ /g, ' ');
  /* Значение из модели — только когда поле не в фокусе: иначе сбили бы курсор */
  useLayoutEffect(() => {
    if (ref.current && document.activeElement !== ref.current && !o.children) ref.current.textContent = o.initial;
  }, [o.initial]);

  const refresh = () => {
    const t = text();
    const list = t.trim().length >= 2 ? c.catalog.matcher.suggest(t, 5) : [];
    if (!t.trim()) return setSug(null);
    setSug({ forId: o.id, text: t, list, sel: -1, rect: sugRect(ref.current!) });
  };
  const pick = (e: Exercise) => {
    setSug(null);
    o.onPick(e);
  };
  const onKey = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Tab' && !e.shiftKey && sug && sug.list.length) {
      e.preventDefault();
      pick(sug.list[sug.sel >= 0 ? sug.sel : 0]);
    } else if (e.key === 'ArrowDown' && sug?.list.length) {
      e.preventDefault();
      setSug({ ...sug, sel: Math.min(sug.sel + 1, sug.list.length - 1) });
    } else if (e.key === 'ArrowUp' && sug?.list.length) {
      e.preventDefault();
      setSug({ ...sug, sel: Math.max(sug.sel - 1, -1) });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (sug && sug.sel >= 0) return pick(sug.list[sug.sel]);
      setSug(null);
      o.onEnter(text().trim());
    } else if (e.key === 'Escape') {
      setSug(null);
      ref.current?.blur();
    } else if (e.key === '+' && o.onPlus) {
      if (o.onPlus()) e.preventDefault();
    }
  };
  return (
    <>
      <span
        ref={ref}
        className={o.className}
        contentEditable
        suppressContentEditableWarning
        data-edit={o.id}
        spellCheck={false}
        data-ph={o.ph}
        onInput={refresh}
        onKeyDown={onKey}
        onPaste={(e) => {
          const t = e.clipboardData.getData('text/plain');
          if (o.onPaste && o.onPaste(t)) e.preventDefault();
          else {
            e.preventDefault();
            document.execCommand('insertText', false, t.replace(/\n/g, ' '));
          }
        }}
        onBlur={() => {
          setTimeout(() => setSug(null), 120);
          o.onCommit?.(text().trim());
        }}
      >
        {o.children}
      </span>
      {sug && (sug.list.length > 0 || sug.text.trim().length >= 2) && <Suggest s={sug} onPick={pick} onNew={async (n) => {
        setSug(null);
        const id = await c.addOwnExercise(n);
        if (id) o.onPick({ id } as Exercise);
      }} />}
    </>
  );
}

/* ─── Вид пустой строки в быстром старте: Упражнение · Сет · Связка ─── */

const KIND_HELP: Record<string, [string, string]> = {
  ss: ['Сет', 'Несколько упражнений по кругу с отдыхом между кругами'],
  ch: ['Связка', 'Несколько упражнений без отдыха и без смены веса — один подход'],
};
function Kind({ it, cur, inSet }: { it: Line; cur: 'ex' | 'ss' | 'ch'; inSet: boolean }) {
  const c = useCtx();
  const set = (k: string) => {
    if (k === cur) return;
    if (k === 'ch') c.update((d) => M.lineToChain(d, it.id));
    else if (k === 'ex') c.update((d) => M.replaceLine(d, it.id, [{ t: 'ex', id: it.id, exId: null, raw: '' }]));
    else if (k === 'ss') c.update((d) => M.lineToSet(d, it.id)[0]);
    setTimeout(() => c.setFocus(it.id, 'e'));
  };
  const kinds: [string, string][] = [
    ['ex', 'Упражнение'],
    ['ss', 'Сет'],
    ['ch', 'Связка'],
  ];
  return (
    <span className="kind">
      {kinds
        .filter(([k]) => !(k === 'ss' && inSet))
        .map(([k, n]) => (
          <span key={k} style={{ display: 'contents' }}>
            <button data-kind={k} data-for={it.id} tabIndex={-1} className={k === cur ? 'on' : ''} onMouseDown={(e) => e.preventDefault()} onClick={() => set(k)}>
              {n}
            </button>
            {KIND_HELP[k] && (
              <span className="qh" tabIndex={0} role="button" aria-label={`Что такое «${KIND_HELP[k][0]}»`} data-tip={KIND_HELP[k][1]} data-tip-card={k}>
                ?
              </span>
            )}
          </span>
        ))}
    </span>
  );
}

function AiBtn({ attr, id, title, sm, dim, busy, onClick }: { attr: string; id: string; title: string; sm?: boolean; dim?: boolean; busy?: boolean; onClick: () => void }) {
  return (
    <button
      className={'aib' + (sm ? ' sm' : '') + (dim ? ' dim' : '') + (busy ? ' busy' : '')}
      aria-busy={busy || undefined}
      {...{ ['data-' + attr]: id }}
      tabIndex={sm ? -1 : undefined}
      title={title}
      aria-label={title}
      onClick={() => !busy && onClick()}
    >
      <Svg src={AIMARK} />
      <span>{busy ? 'Разбираю…' : 'AI'}</span>
    </button>
  );
}
export { AiBtn };

/* ─── Строка упражнения ─── */

export function LineView({ it, inSet, quick }: { it: Line; inSet: boolean; quick: boolean }) {
  const c = useCtx();
  const [act, setAct] = useState(false);
  if (it.t === 'chain') return <ChainView it={it} inSet={inSet} quick={quick} />;
  const ex = it.exId ? c.catalog.ex(it.exId) : null;
  const raw = String(it.raw || '');
  const focusProps = {
    onFocus: () => setAct(true),
    onBlur: (e: React.FocusEvent) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setAct(false);
    },
  };
  const next = () => {
    let id = '';
    c.update((d) => {
      const [n, nid] = M.addLine(d, { after: it.id });
      id = nid;
      return n;
    });
    setTimeout(() => c.setFocus(id, 'e'));
  };
  const pick = (e: Exercise) => {
    c.update((d) => M.pickExercise(d, it.id, e.id));
    setTimeout(() => c.setFocus(it.id, 'sch'));
  };
  const del = (
    <button className="x" data-del={it.id} tabIndex={-1} title={ex ? 'Удалить упражнение' : undefined} onClick={() => c.update((d) => M.removeItem(d, it.id))}>
      <Svg src={ICON.x} />
    </button>
  );
  const paste = (t: string) => {
    if (!t.includes('\n')) return false;
    c.update((d) => M.pasteText(d, it.id, t));
    return true;
  };

  /* Строка текстом — обычное сохранённое состояние, а не ошибка (CON-5) */
  if (!ex)
    return (
      <div className={'line raw' + (act ? ' act' : '') + (it.scheme || it.load || it.txt ? ' wp' : '')} data-item={it.id} {...focusProps}>
        <span className="gr" draggable data-drag={it.id}>
          <Svg src={ICON.grip} />
        </span>
        <NameEdit
          id={it.id}
          field="e"
          initial={raw}
          className="txt"
          ph={quick ? (inSet ? 'упражнение сета' : 'упражнение') : undefined}
          onPick={pick}
          onEnter={(t) => {
            c.update((d) => M.setLine(d, it.id, { raw: t }));
            if (t) next();
          }}
          onCommit={(t) => t !== raw && c.update((d) => M.setLine(d, it.id, { raw: t }))}
          onPaste={paste}
        />
        {quick && !raw.trim() && <Kind it={it} cur="ex" inSet={inSet} />}
        {raw.trim() && (
          <>
            {/* Упражнение не из базы, но с повторами и весом (разбор ИИ) — параметры как у упражнения из базы */}
            {(it.scheme || it.load || it.txt) && (
              <span className="prm">
                <Params it={it} schPh={inSet ? 'повт' : '3×10'} />
              </span>
            )}
            {(it.scheme || it.load || it.txt) && <span className="sp"></span>}
            {/* С параметрами в строке тесно — коротко «Из базы» */}
            <button className="pick" data-pickfor={it.id} tabIndex={-1} title="Выбрать упражнение из базы" onClick={() => c.setFocus(it.id, 'e')}>
              {it.scheme || it.load || it.txt ? 'Из базы' : 'Выбрать упражнение'}
            </button>
            <LineVideo it={it} title={raw} />
            {/* AI у отдельной строки пока скрыт — остаётся только у «Блока текстом» (BlockView).
            <AiBtn attr="ailine" id={it.id} sm title="Разобрать строку: ИИ найдёт упражнение в базе, схему и нагрузку" onClick={() => c.aiLine(it.id)} /> */}
          </>
        )}
        {del}
      </div>
    );

  const rest = ex.id === REST_ID;
  return (
    <div className={'line' + (act ? ' act' : '') + (rest ? ' restl' : '')} data-item={it.id} {...focusProps}>
      <span className="gr" draggable data-drag={it.id}>
        <Svg src={ICON.grip} />
      </span>
      {rest && (
        <span className="resti" title="Отдых между упражнениями">
          <Svg src={DAYICON.rest} />
        </span>
      )}
      <NameEdit
        key={ex.id}
        id={it.id}
        field="e"
        initial={ex.ru}
        className="txt"
        onPick={pick}
        onEnter={() => c.setFocus(it.id, 'sch')}
        onCommit={(t) => {
          /* Переписанное руками название не подбирается заново — строка уходит в текст (CON-19) */
          if (t && t !== ex.ru) {
            const before = c.doc;
            c.update((d) => M.lineToText(d, it.id, t));
            c.toast('Название не из базы — строка сохранена текстом', () => c.update(() => before));
          }
        }}
        onPlus={() => {
          c.update((d) => M.lineToChain(d, it.id));
          setTimeout(() => c.setFocus(it.id, 'cadd'));
          return true;
        }}
      >
        <span className="nm">{ex.ru}</span>
      </NameEdit>
      <span className="prm">
        <Params it={it} schPh={inSet ? 'повт' : '3×10'} />
      </span>
      <span className="sp"></span>
      <LineVideo it={it} title={ex.ru} ex={ex} />
      {del}
    </div>
  );
}

/* Видео к строке (VID-2).
   Упражнение из базы (общей или своей): видео тренера к этому упражнению и демонстрация (гифка) подставляются сами,
   в строке любое можно убрать крестиком (только здесь). Загрузка/замена видео в строке сохраняет его в упражнение —
   дальше оно само будет во всех тренировках этого тренера (другие тренеры его не видят).
   Строка текстом: видео разовое — живёт в самой строке и копируется вместе с тренировкой. */
function LineVideo({ it, title, ex }: { it: ExLine; title: string; ex?: Exercise }) {
  const c = useCtx();
  const [open, setOpen] = useState<'' | 'v' | 'g' | 'add'>('');
  const saveEx = useExVideo();
  const saveExLink = useExLink();
  const up = useVideoUpload(
    async (video) => {
      if (!ex) {
        c.update((d) => M.setLine(d, it.id, { video }));
        return c.toast('Видео загружено — клиент увидит его в тренировке');
      }
      try {
        await saveEx(ex.id, video);
        c.update((d) => M.setLine(d, it.id, { video: null, noVideo: false }));
        c.toast(`Видео сохранено в упражнение «${ex.ru}» — теперь оно само будет во всех ваших тренировках`);
      } catch {
        c.toast('Не удалось сохранить видео в упражнение — попробуйте ещё раз');
      }
    },
    (msg) => c.toast(msg),
    title,
  );
  const on = !!useMe().data?.videoOn;
  /* Видео строки: своё к строке (строка текстом или старые тренировки), иначе — видео упражнения, если не убрано */
  const v = it.video || (ex && !it.noVideo ? ex.video : null) || null;
  const fromEx = !it.video && !!v;
  const gif = ex?.gif && !it.noGif ? ex.gif : null;
  const off = (patch: Partial<ExLine>, msg: string) => {
    const before = c.doc;
    c.update((d) => M.setLine(d, it.id, patch));
    setOpen('');
    c.toast(msg, () => c.update(() => before));
  };
  const dropVideo = () => off(fromEx ? { noVideo: true } : { video: null }, 'Видео убрано из этой строки');
  /* Ссылка на ролик (VID-3) — так же, как видео: у упражнения из базы — в упражнение, у строки текстом — в строку */
  const l = it.link || (ex && !it.noLink ? ex.link : null) || null;
  const lFromEx = !it.link && !!l;
  const dropLink = () => off(lFromEx ? { noLink: true } : { link: null }, 'Ссылка на видео убрана из этой строки');
  const addLink = async (url: string) => {
    if (!ex) {
      const link = videoLink(url);
      c.update((d) => M.setLine(d, it.id, { link }));
      return c.toast('Ссылка добавлена — клиент откроет видео на ' + link!.host);
    }
    await saveExLink(ex.id, url);
    c.update((d) => M.setLine(d, it.id, { link: null, noLink: false }));
    c.toast(`Ссылка сохранена в упражнение «${ex.ru}» — теперь она будет во всех ваших тренировках`);
  };
  /* Кнопка «добавить»: пока нет ссылки или (если видеохостинг подключён) своего видео */
  const canUp = on && !v;
  const canAdd = !l || canUp;
  const x = (f: () => void, t: string) => (
    <i
      className="vx"
      role="button"
      title={t}
      onClick={(e) => {
        e.stopPropagation();
        f();
      }}
    >
      <Svg src={ICON.x} />
    </i>
  );
  return (
    <>
      {up.input}
      {gif && (
        <button className="vid has gif" tabIndex={-1} title="Демонстрация из базы — клиент увидит её в технике" onClick={() => setOpen('g')}>
          GIF
          {x(() => off({ noGif: true }, 'Демонстрация убрана из этой строки'), 'Убрать демонстрацию из этой строки')}
        </button>
      )}
      {up.busy ? (
        <span className="vid up" title="Видео загружается">
          <Svg src={ICON.video} /> {up.pct}%
        </span>
      ) : v ? (
        <button className="vid has" tabIndex={-1} title={fromEx ? 'Ваше видео к упражнению — подставлено из базы' : 'Видео к строке'} onClick={() => setOpen('v')}>
          <Svg src={ICON.play} /> Видео
          {x(dropVideo, 'Убрать видео из этой строки')}
        </button>
      ) : ex && it.noVideo && ex.video ? (
        <button className="vid" tabIndex={-1} title="Вернуть ваше видео к упражнению" onClick={() => c.update((d) => M.setLine(d, it.id, { noVideo: false }))}>
          <Svg src={ICON.video} /> вернуть
        </button>
      ) : null}
      {l ? (
        <button className="vid has" tabIndex={-1} title={(lFromEx ? 'Ваша ссылка к упражнению — подставлена из базы. ' : '') + 'Открыть: ' + l.url} onClick={() => openLink(l)}>
          <Svg src={EXT_SVG} /> {l.host}
          {x(dropLink, 'Убрать ссылку из этой строки')}
        </button>
      ) : ex && it.noLink && ex.link ? (
        <button className="vid" tabIndex={-1} title="Вернуть вашу ссылку на видео к упражнению" onClick={() => c.update((d) => M.setLine(d, it.id, { noLink: false }))}>
          <Svg src={EXT_SVG} /> вернуть
        </button>
      ) : null}
      {!up.busy && canAdd && (
        <button
          className="vid"
          tabIndex={-1}
          title={ex ? 'Видео к упражнению: ссылка на YouTube, Rutube, VK… или свой файл — сохранится в базе и будет во всех ваших тренировках' : 'Видео к этой строке: ссылка на YouTube, Rutube, VK… или свой файл'}
          onClick={() => setOpen('add')}
        >
          <Svg src={ICON.video} />
        </button>
      )}
      {open === 'add' && (
        <AddVideoModal
          title={ex ? 'Видео к упражнению «' + ex.ru + '»' : 'Видео к строке'}
          hint={ex ? 'Сохранится в упражнение — будет во всех ваших тренировках с ним. Видите только вы и ваши клиенты.' : undefined}
          link={!l}
          upload={canUp ? up.pick : undefined}
          onLink={addLink}
          onClose={() => setOpen('')}
        />
      )}
      {ex?.gif && it.noGif && (
        <button className="vid" tabIndex={-1} title="Вернуть демонстрацию из базы" onClick={() => c.update((d) => M.setLine(d, it.id, { noGif: false }))}>
          GIF
        </button>
      )}
      {open === 'v' && v && (
        <VideoModal
          video={v}
          title={title || 'Видео'}
          onClose={() => setOpen('')}
          actions={
            <>
              <button className="btn gh" onClick={dropVideo}>
                Убрать из строки
              </button>
              <span className="sp"></span>
              {on && (
                <button
                  className="btn"
                  title={ex ? 'Новое видео сохранится в упражнение — во всех ваших тренировках' : undefined}
                  onClick={() => {
                    setOpen('');
                    up.pick();
                  }}
                >
                  Заменить
                </button>
              )}
            </>
          }
        />
      )}
      {open === 'g' && gif && (
        <Modal
          title={title}
          onClose={() => setOpen('')}
          foot={
            <button className="btn gh" onClick={() => off({ noGif: true }, 'Демонстрация убрана из этой строки')}>
              Убрать из строки
            </button>
          }
        >
          <img className="egif" src={mediaUrl(gif)} alt={title + ' — демонстрация'} />
        </Modal>
      )}
    </>
  );
}

/* ─── Связка (CON-23) ─── */

function ChainView({ it, inSet, quick }: { it: ChainLine; inSet: boolean; quick: boolean }) {
  const c = useCtx();
  const [act, setAct] = useState(false);
  const L = loadInfo(it, c);
  const kg = kgText(workKg(it, c.pm, c.catalog.ex));
  const ld = useField(ldVal(it.load));
  const ldRef = useRef<HTMLInputElement>(null);
  const sets = useField(it.sets ? String(it.sets) : '');
  const per = useField(it.per ? String(it.per) : '');
  const ws = it.parts.filter((p) => p.exId && pmKey(c.catalog.ex(p.exId)));
  const seen = new Set<string>();
  const wsU = ws.filter((p) => !seen.has(p.exId!) && seen.add(p.exId!));
  const auto = chainOf({ ...it, of: null }, c.pm, c.catalog.ex);
  const known = auto && c.pm[pmKey(c.catalog.ex(auto.exId!))!];
  const summ = [[loadText(it.load), kg ? kg + ' кг' : ''].filter(Boolean).join(' · '), chainScheme(it)].filter(Boolean).join(' — ');
  const hasRange = it.load?.to != null;
  const [pmv, setPm] = useState('');

  return (
    <div
      className={'line chain' + (it.parts.length ? '' : ' empty') + (act ? ' act' : '')}
      data-item={it.id}
      onFocus={() => setAct(true)}
      onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setAct(false)}
      onMouseDown={(e) => {
        /* Поля связки видны, только пока курсор в ней (:focus-within): нажатие на название или сводку
           «75 % · 80 кг — 3×(1+1+1)» сначала входит в связку, потом ставит курсор в нужное поле (как в прототипе).
           На нажатии, а не на клике, и без фокуса по умолчанию: иначе связка теряет фокус, поля прячутся,
           строка сдвигается — и клик попадает мимо. */
        const t = e.target as Element;
        if (e.button !== 0 || t.closest('input, select, textarea, [contenteditable], button, .gr, .dsel, a')) return;
        e.preventDefault();
        const box = e.currentTarget;
        const part = t.closest<HTMLElement>('[data-part]')?.dataset.part;
        const cadd = box.querySelector<HTMLElement>('.cadd');
        cadd?.focus();
        requestAnimationFrame(() => {
          const f = part ? box.querySelector<HTMLInputElement>(`[data-pf="sch"][data-for="${part}"]`) : t.closest('.cprm') ? box.querySelector<HTMLInputElement>('[data-pf="sets"]') : null;
          if (f) focusAtEnd(f);
        });
      }}
    >
      <span className="gr" title="Перетащить связку" draggable data-drag={it.id}>
        <Svg src={ICON.grip} />
      </span>
      <span className="chl">Связка</span>
      <span className="cps">
        {it.parts.map((p, k) => (
          <span key={p.id} style={{ display: 'contents' }}>
            {k > 0 && <span className="cplus">+</span>}
            <span className={'cp' + (p.exId ? '' : ' raw')} data-part={p.id}>
              <span className="cpn">{partName(p, c.catalog.ex)}</span>
              <RepsInput chainId={it.id} partId={p.id} reps={p.reps || ''} />
              <button className="cpx" data-delpart={p.id} tabIndex={-1} title="Убрать из связки" onClick={() => c.update((d) => M.removePart(d, it.id, p.id))}>
                <Svg src={ICON.x} />
              </button>
            </span>
          </span>
        ))}
        <span className="caddw">
          {it.parts.length > 0 && <span className="cplus">+</span>}
          <NameEdit
            id={it.id}
            field="cadd"
            initial=""
            className="cadd"
            ph={it.parts.length ? 'упражнение' : 'первое упражнение связки'}
            onPick={(e) => {
              c.update((d) => M.addPart(d, it.id, e.id, null));
              setTimeout(() => c.setFocus(it.id, 'cadd'));
            }}
            onEnter={(t) => {
              if (t) {
                c.update((d) => M.addPart(d, it.id, null, t));
                setTimeout(() => c.setFocus(it.id, 'cadd'));
              } else {
                let id = '';
                c.update((d) => {
                  const [n, nid] = M.addLine(d, { after: it.id });
                  id = nid;
                  return n;
                });
                setTimeout(() => c.setFocus(id, 'e'));
              }
            }}
          />
        </span>
        {it.parts.length > 0 && (
          <span className="cprm">
            {summ && <span className="cpt">{summ}</span>}
            <span className="pf sets" title="Сколько раз связка выполняется подряд в одном подходе">
              <input
                data-pf="per"
                data-for={it.id}
                value={per.v}
                placeholder="1"
                inputMode="numeric"
                autoComplete="off"
                style={{ width: fw(per.v, 2, 4) }}
                onChange={(e) => per.setV(e.target.value.replace(/\D/g, ''))}
                {...per.bind}
                onBlur={(e) => {
                  const v = +e.target.value;
                  afterFocus(() => c.update((d) => M.setLine(d, it.id, { per: v > 1 ? v : null })));
                }}
              />
              <u>св. в подх.</u>
            </span>
            <span className="pf sets">
              <input
                data-pf="sets"
                data-for={it.id}
                value={sets.v}
                placeholder="1"
                inputMode="numeric"
                autoComplete="off"
                style={{ width: fw(sets.v, 2, 4) }}
                onChange={(e) => sets.setV(e.target.value.replace(/\D/g, ''))}
                {...sets.bind}
                onBlur={(e) => {
                  const v = +e.target.value || null;
                  afterFocus(() => c.update((d) => M.setLine(d, it.id, { sets: v })));
                }}
              />
              <u>подх.</u>
            </span>
            {L.has && (
              <>
                <span className={'pf ld ' + (ld.v ? '' : 'empty')}>
                  <input
                    ref={ldRef}
                    data-pf="ld"
                    data-for={it.id}
                    value={ld.v}
                    placeholder={L.weighted ? 'вес' : 'объём'}
                    inputMode="decimal"
                    autoComplete="off"
                    spellCheck={false}
                    style={{ width: fw(ld.v, 4, 4) }}
                    onChange={(e) => ld.setV(e.target.value)}
                    {...ld.bind}
                    onBlur={(e) => {
                      const r = applyLoad(e.target.value, L.cur);
                      if ('load' in r) afterFocus(() => c.update((d) => M.setLine(d, it.id, { load: r.load })));
                    }}
                  />
                  <u
                    data-pfu={it.id}
                    title="Сменить единицу"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      const next = L.units[(L.units.indexOf(L.cur) + 1) % L.units.length];
                      c.unitSel[it.id] = next;
                      if (it.load) {
                        const load: Load = next === '%' ? { kind: 'pct', from: it.load.from } : { kind: 'abs', unit: next as AbsUnit, from: it.load.from };
                        if (it.load.to != null) load.to = it.load.to;
                        c.update((d) => M.setLine(d, it.id, { load }));
                      } else c.update((d) => d);
                    }}
                  >
                    {L.cur}
                  </u>
                </span>
                <button
                  className={'rng ' + (hasRange ? 'on' : '')}
                  data-rng={it.id}
                  tabIndex={-1}
                  title={hasRange ? 'Убрать диапазон — оставить одно число' : RNG_TIP}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (hasRange && it.load) {
                      const { to: _t, ...rest } = it.load;
                      c.update((d) => M.setLine(d, it.id, { load: rest as Load }));
                    } else {
                      ld.setV(appendRngDash(ld.v));
                      requestAnimationFrame(() => focusAtEnd(ldRef.current));
                    }
                  }}
                >
                  от–до
                </button>
              </>
            )}
            {L.weighted && wsU.length > 1 && (
              <span className={'cof ' + (L.cur === '%' ? '' : 'off')}>
                от 1ПМ{' '}
                <select
                  data-cof={it.id}
                  tabIndex={-1}
                  title="От 1ПМ какого упражнения считать процент"
                  value={it.of || ''}
                  onChange={(e) => c.update((d) => M.setLine(d, it.id, { of: e.target.value || null }))}
                >
                  <option value="">{c.isGroup || !known ? 'самого слабого' : partName(auto!, c.catalog.ex) + ' · самое слабое'}</option>
                  {wsU.map((p) => (
                    <option key={p.exId} value={p.exId!}>
                      {partName(p, c.catalog.ex)}
                    </option>
                  ))}
                </select>
              </span>
            )}
            <span className="kg">{kg != null ? '→ ' + kg + ' кг' : c.isGroup && it.load?.kind === 'pct' ? 'от 1ПМ каждого' : ''}</span>
            {L.weighted && (
              <span className={'pmf ' + (L.cur === '%' && !c.pm[L.key!] && !c.isGroup ? '' : 'off')}>
                1ПМ клиента{' '}
                <span className="pf pm">
                  <input
                    data-pf="pm"
                    data-for={it.id}
                    placeholder="—"
                    inputMode="decimal"
                    autoComplete="off"
                    value={pmv}
                    style={{ width: fw(pmv, 3, 4) }}
                    onChange={(e) => setPm(e.target.value)}
                    onBlur={() => {
                      const v = parseFloat(pmv.replace(',', '.'));
                      if (v > 0 && L.key) c.setMax(L.key, v);
                      setPm('');
                    }}
                  />
                  <u>кг</u>
                </span>
              </span>
            )}
          </span>
        )}
      </span>
      {quick && !it.parts.length && <Kind it={it} cur="ch" inSet={inSet} />}
      <button className="x" data-del={it.id} tabIndex={-1} title="Удалить связку" onClick={() => c.update((d) => M.removeItem(d, it.id))}>
        <Svg src={ICON.x} />
      </button>
    </div>
  );
}

function RepsInput({ chainId, partId, reps }: { chainId: string; partId: string; reps: string }) {
  const c = useCtx();
  const f = useField(reps);
  return (
    <input
      className="pf sch rep"
      data-pf="sch"
      data-for={partId}
      value={f.v}
      placeholder="повт"
      autoComplete="off"
      spellCheck={false}
      inputMode="numeric"
      style={{ width: fw(f.v, 4, 20) }}
      onChange={(e) => f.setV(e.target.value)}
      {...f.bind}
      onBlur={(e) => {
        const v = e.target.value.trim();
        afterFocus(() => c.update((d) => M.setPart(d, chainId, partId, v)));
      }}
    />
  );
}
